import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MainContent from '../components/MainContent';
import CampaignsTab from '../components/jobs/CampaignsTab';
import RecommendationsTab from '../components/jobs/RecommendationsTab';
import ApplicationsTab from '../components/jobs/ApplicationsTab';
import ReportsTab from '../components/jobs/ReportsTab';
import PostedJobsTab from '../components/jobs/PostedJobsTab';
import { canManageJobDigest } from '../utils/permissionKeys';

const DIGEST_TABS = [
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'recommendations', label: 'Recommendations' },
  { key: 'applications', label: 'Proof of applying' },
  { key: 'reports', label: 'Reports' },
];

// Adding jobs is open to AddJobs; the digest and campaigns need
// ManageJobDigest.
const POSTED_TAB = { key: 'posted', label: 'Added jobs' };

// Job recommendations emailed to graduates: send them to chosen cohorts,
// see who got what, review proof that graduates applied, and report the
// outcomes to partners. The cohort and campaign choice is kept in the URL,
// so it carries across tabs and links can be shared.
export default function JobDigest() {
  const [params, setParams] = useSearchParams();
  const permissions = useSelector((state) => state.auth.permissions ?? []);
  const role = useSelector((state) => state.auth.userDetails?.role);
  const digest = canManageJobDigest(permissions, role);
  const TABS = digest ? [...DIGEST_TABS, POSTED_TAB] : [POSTED_TAB];
  const tab = TABS.some((t) => t.key === params.get('tab'))
    ? params.get('tab')
    : TABS[0].key;
  const cohortIds = (params.get('cohorts') || '').split(',').filter(Boolean);
  const campaignId = params.get('campaign') || '';

  const update = (changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setParams(next, { replace: true });
  };
  const setCohortIds = (ids) => update({ cohorts: ids.join(',') });
  const setCampaignId = (id) => update({ campaign: id });
  const shared = { cohortIds, setCohortIds, campaignId, setCampaignId };

  return (
    <MainContent full>
      <div className="space-y-5 pb-6">
        <header>
          <h1 className="text-2xl font-semibold text-gray-900">
            Jobs for graduates
          </h1>
          <p className="text-xs text-gray-500">
            Tech job openings matched to each graduate&apos;s training, emailed
            by cohort, with proof of applications and outcomes for partners.
          </p>
        </header>

        <nav className="flex flex-wrap gap-1 border-b border-gray-200">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => update({ tab: t.key })}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
                tab === t.key
                  ? 'border-claret-500 text-claret-500'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {tab === 'campaigns' && (
          <CampaignsTab
            {...shared}
            onView={(campaign) =>
              update({
                tab: 'recommendations',
                campaign: campaign.id,
                cohorts: campaign.cohorts.map((c) => c.id).join(','),
              })
            }
          />
        )}
        {tab === 'recommendations' && <RecommendationsTab {...shared} />}
        {tab === 'applications' && <ApplicationsTab {...shared} />}
        {tab === 'reports' && <ReportsTab {...shared} />}
        {tab === 'posted' && <PostedJobsTab />}
      </div>
    </MainContent>
  );
}
